import React, { createContext, useContext, useMemo, useState } from 'react'

/**
 * The one channel an auth PAGE has to say something to the ambient BACKDROP.
 *
 * WHY A CONTEXT AND NOT A PROP. `SignInBackground` is rendered by
 * `LayoutAuthAmbient`, above the route boundary — that is the whole point of
 * that layout (it stops the 300px-blur layer being re-rasterised on every
 * navigation). So the page that wants to affect the backdrop is a DESCENDANT of
 * the thing it wants to affect, and there is no prop path between them. A
 * context provider wrapping both the backdrop and the `<Outlet />` is the only
 * seam where both halves are in scope.
 *
 * WHY A NUMBER AND NOT A BOOLEAN. `surge` is a 0..1 intensity, not an on/off.
 * The success screen ramps it once and leaves it there, but expressing it as a
 * scalar means the backdrop owns the CURVE (how far it compresses, how much
 * faster it runs) while the page owns only the INTENT. A boolean would have put
 * both halves of that decision in the page.
 *
 * Default 0 and a no-op setter, so every auth route that does not care — which
 * is all of them except signup success — renders exactly as before and needs no
 * provider awareness.
 */
export interface AmbientSurgeValue {
  /** 0 = ambient drift (the default). 1 = fully converged on the page centre. */
  surge: number
  setSurge: (value: number) => void
}

const AmbientSurgeContext = createContext<AmbientSurgeValue>({
  surge: 0,
  setSurge: () => undefined,
})

export const AmbientSurgeProvider = ({ children }: { children: React.ReactNode }) => {
  const [surge, setSurge] = useState(0)
  const value = useMemo(() => ({ surge, setSurge }), [surge])
  return <AmbientSurgeContext.Provider value={value}>{children}</AmbientSurgeContext.Provider>
}

/** Read the current surge — for the backdrop. */
export const useAmbientSurge = (): AmbientSurgeValue => useContext(AmbientSurgeContext)

export default AmbientSurgeContext
